"use client";

import * as React from "react";
import { ArrowUpRight, Check, Copy, User2 } from "lucide-react";

import { Badge } from "../data-display/badge";
import { IdentityAvatar } from "../data-display/identity-avatar";
import { useCopyToClipboard } from "../hooks/use-copy-to-clipboard";
import { useLinkComponent } from "../navigation/link-context";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../primitives/hover-card";
import { cn, resolveIdentityHref, resolveUserLabel, type IdentityKind } from "../utils";

export type { IdentityKind } from "../utils";

export interface IdentityDisplayProps {
  kind?: IdentityKind | null | undefined;
  name?: string | null | undefined;
  email?: string | null | undefined;
  publicId?: string | null | undefined;
  avatarUrl?: string | null | undefined;
  /**
   * Current workspace slug. When set (together with a `publicId` and a linkable
   * `kind`), the hover card links to that actor's in-app profile page —
   * `/w/:slug/users/:id` for users, `/w/:slug/agents/:id` for agents. Resolved
   * via {@link resolveIdentityHref}; there is no per-kind href prop, so any new
   * linkable kind is added in one place.
   */
  workspaceSlug?: string | null | undefined;
  /** `inline` = no hover card; `card` = wrapped in a hover preview card. */
  variant?: "inline" | "card" | undefined;
  /** Render smaller — used in dense mobile rows. */
  compact?: boolean | undefined;
  className?: string | undefined;
}

const KIND_LABEL: Partial<Record<IdentityKind, { badge: string; secondary: string }>> = {
  agent: { badge: "Agent", secondary: "AI agent" },
  system: { badge: "System", secondary: "System" },
  api_key: { badge: "API key", secondary: "API key" },
  oauth_client: { badge: "OAuth client", secondary: "OAuth client" },
  custom_app: { badge: "App", secondary: "Custom app" },
};

function UnassignedAvatar() {
  return (
    <span
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-dashed border-muted-foreground/40"
      aria-hidden
    >
      <User2 className="h-3 w-3 text-muted-foreground/50" />
    </span>
  );
}

/**
 * Small affordance revealed on hover of the identity card that copies the
 * display name to the clipboard. Implemented as a `role="button"` span so it can
 * live inside the card's anchor (for agents) without nesting interactive
 * elements. Uses the shared {@link useCopyToClipboard} hook so the "copied"
 * confirmation only shows on a successful write (and the timer is cleaned up on
 * unmount).
 */
function CopyNameButton({ name }: { name: string }) {
  const { copied, copy } = useCopyToClipboard(name, { copiedDuration: 1200 });

  // `copy` stops propagation; we also prevent the default so the click never
  // follows the surrounding agent-config anchor.
  const handleCopy = (e: React.SyntheticEvent) => {
    e.preventDefault();
    void copy(e);
  };

  return (
    <span
      role="button"
      tabIndex={0}
      aria-label={`Copy "${name}"`}
      title="Copy name"
      onClick={handleCopy}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleCopy(e);
      }}
      className={cn(
        "inline-flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground opacity-0 transition hover:text-foreground focus-visible:opacity-100 group-hover/identity:opacity-100",
        copied && "text-foreground opacity-100",
      )}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </span>
  );
}

/**
 * Canonical identity renderer — an avatar + truncated label for a user, agent,
 * or system actor. Always constrains itself with `min-w-0 max-w-full` +
 * `truncate` so the label never overflows its container.
 *
 * `inline` renders just the chip; `card` wraps it in a hover preview card with
 * a kind badge and a copy-name affordance. When `workspaceSlug` is set and the
 * actor is linkable (user or agent), the card body becomes an in-app link to
 * that actor's profile page (via the router-aware {@link useLinkComponent}).
 */
export function IdentityDisplay({
  kind,
  name,
  email,
  publicId,
  avatarUrl,
  workspaceSlug,
  variant = "card",
  compact,
  className,
}: IdentityDisplayProps): React.JSX.Element {
  const LinkComponent = useLinkComponent();
  const label = resolveUserLabel({ name, email, id: publicId });

  // No resolvable identity → "Unassigned" placeholder, matching prior behavior.
  if (!label) {
    return (
      <span
        className={cn(
          "inline-flex min-w-0 max-w-full items-center gap-2 text-muted-foreground",
          compact ? "text-[11px]" : "text-sm",
          className,
        )}
      >
        <UnassignedAvatar />
        <span className="truncate">Unassigned</span>
      </span>
    );
  }

  const avatar = (
    <IdentityAvatar kind={kind} id={publicId} name={label} email={email} avatarUrl={avatarUrl} />
  );

  const chip = (
    <span
      className={cn(
        "inline-flex min-w-0 max-w-full cursor-default items-center gap-2",
        compact ? "text-[11px]" : "text-sm",
        className,
      )}
    >
      {avatar}
      <span className="truncate text-foreground" title={label}>
        {label}
      </span>
    </span>
  );

  if (variant === "inline" || compact) return chip;

  const kindLabel = kind ? KIND_LABEL[kind] : undefined;
  const badgeLabel = kindLabel?.badge ?? "User";
  const secondary = kindLabel?.secondary ?? email ?? "Member";

  const href = resolveIdentityHref({ kind, publicId, workspaceSlug });
  const isLink = Boolean(href);
  const linkClassName = cn(
    "group/identity flex items-start gap-3 rounded-md no-underline",
    isLink &&
      "-m-1 cursor-pointer p-1 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  );

  const cardBody = (
    <>
      <IdentityAvatar
        kind={kind}
        id={publicId}
        name={label}
        email={email}
        avatarUrl={avatarUrl}
        size="md"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-medium text-foreground">{label}</span>
          <CopyNameButton name={label} />
          <Badge
            variant="outline"
            className="ml-auto h-5 shrink-0 rounded-md px-1.5 text-[10px] font-semibold"
          >
            {badgeLabel}
          </Badge>
          {isLink ? (
            <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
          ) : null}
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{secondary}</p>
      </div>
    </>
  );

  // Render the card body through the router-aware link when linkable (and a
  // LinkProvider is in scope), falling back to a plain anchor, then a static
  // div. Same-tab in-app navigation — no `target="_blank"` — since these point
  // at internal profile pages.
  let cardContent: React.JSX.Element;
  if (isLink && LinkComponent) {
    cardContent = (
      <LinkComponent to={href!} className={linkClassName}>
        {cardBody}
      </LinkComponent>
    );
  } else if (isLink) {
    cardContent = (
      <a href={href!} className={linkClassName}>
        {cardBody}
      </a>
    );
  } else {
    cardContent = <div className={linkClassName}>{cardBody}</div>;
  }

  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>{chip}</HoverCardTrigger>
      <HoverCardContent align="start" className="w-64">
        {cardContent}
      </HoverCardContent>
    </HoverCard>
  );
}
